import {HierarchyNodeMetadata, HierarchyNodeType} from '@domain/shared'
import {collectPlantIds, type UpmPlantsHierarchyNode} from '@domain/upm'

type UpmPlantIdMapping = {upmId: string; plantId: string}

type AreaPlants = {id: string; plantIds: string[]}

const collectKpiPlantIds = (node: HierarchyNodeMetadata): string[] =>
  node.type === HierarchyNodeType.Plant ? [node.id] : node.children.flatMap(collectKpiPlantIds)

const findBestMatchingArea = (plantIds: string[], areas: AreaPlants[]): string | undefined => {
  const wanted = new Set(plantIds)
  let best: {id: string; overlap: number} | undefined

  for (const area of areas) {
    const overlap = area.plantIds.filter((id) => wanted.has(id)).length
    if (overlap > 0 && (!best || overlap > best.overlap)) {
      best = {id: area.id, overlap}
    }
  }

  return best?.id
}

const getKpiAreas = (kpiHierarchy: HierarchyNodeMetadata): AreaPlants[] =>
  kpiHierarchy.children
    .filter((node) => node.type === HierarchyNodeType.Area)
    .map((node) => ({id: node.id, plantIds: collectKpiPlantIds(node)}))

const getUpmAreas = (upmHierarchy: UpmPlantsHierarchyNode): AreaPlants[] =>
  upmHierarchy.children.map((node) => ({id: node.hierarchyId, plantIds: collectPlantIds(node)}))

/** UPM (Suite) area node → KPI area id (e.g. 'a_NAM'), matched via the SAP plant ids both contain. */
export const mapUpmAreaToKpiArea = ({
  upmAreaId,
  upmHierarchy,
  kpiHierarchy,
  upmPlants
}: {
  upmAreaId: string
  upmHierarchy: UpmPlantsHierarchyNode
  kpiHierarchy: HierarchyNodeMetadata
  upmPlants: UpmPlantIdMapping[]
}): string | undefined => {
  const upmArea = upmHierarchy.children.find((node) => node.hierarchyId === upmAreaId)
  if (!upmArea) return undefined

  const upmIdToPlantId = new Map(upmPlants.map((plant) => [plant.upmId, plant.plantId]))
  const plantIds = collectPlantIds(upmArea)
    .map((upmId) => upmIdToPlantId.get(upmId))
    .filter((id): id is string => id !== undefined)

  return findBestMatchingArea(plantIds, getKpiAreas(kpiHierarchy))
}

/** KPI area id (e.g. 'a_NAM') → UPM (Suite) area hierarchyId, matched via the plants both contain. */
export const mapKpiAreaToUpmArea = ({
  kpiAreaId,
  upmHierarchy,
  kpiHierarchy,
  upmPlants
}: {
  kpiAreaId: string
  upmHierarchy: UpmPlantsHierarchyNode
  kpiHierarchy: HierarchyNodeMetadata
  upmPlants: UpmPlantIdMapping[]
}): string | undefined => {
  const kpiArea = getKpiAreas(kpiHierarchy).find((area) => area.id === kpiAreaId)
  if (!kpiArea) return undefined

  const plantIdToUpmId = new Map(upmPlants.map((plant) => [plant.plantId, plant.upmId]))
  const upmIds = kpiArea.plantIds
    .map((plantId) => plantIdToUpmId.get(plantId))
    .filter((id): id is string => id !== undefined)

  return findBestMatchingArea(upmIds, getUpmAreas(upmHierarchy))
}
